import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup, useMediaQuery } from '@mui/material';
import { AddOutlined, AutoAwesomeOutlined } from '@mui/icons-material';
import { segmentedToggleSx } from '../../../shared/utils/segmented-toggle-sx';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../loading-page';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { TableActionButtons } from '../../presentation-components/miscellaneous-components/table-action-buttons';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { IconName } from '../../../shared/interfaces/icon-picker.model';
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications';
import SurveyAdminService from '../../../shared/services/survey/survey-admin.service';
import { ISurveyQuestionGroup } from '../../../shared/services/survey/survey.model';
import { SurveyQuestionGroupDialog } from '../../dialogs/surveys/survey-question-group-dialog';
import { NewFromTemplateDialog } from '../../dialogs/surveys/new-from-template-dialog';

import gaiaTheme from '../../../root-components/theme';
import moment from 'moment';

const headCells: IHeadCell[] = [
  { id: 'name', label: 'Name', numeric: false, sortable: false, includeInMobile: true },
  { id: 'description', label: 'Description', numeric: false, sortable: false },
  { id: 'questions', label: 'Questions', numeric: true, sortable: false },
  { id: 'created', label: 'Created', numeric: false, sortable: false },
  { id: 'actions', label: '', numeric: false, sortable: false },
];

export const SurveyQuestionGroupsPage = ({ embedded = false }: { embedded?: boolean }) => {
  const navigate = useNavigate();
  const surveyAdminService = SurveyAdminService.getInstance();
  const userNotificationsContext = useContext(UserNotificationsContext);
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));

  const [tabIndex, setTabIndex] = useState(0);
  const [groups, setGroups] = useState<ISurveyQuestionGroup[]>();
  const [archivedGroups, setArchivedGroups] = useState<ISurveyQuestionGroup[]>();
  const [openDialog, setOpenDialog] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<ISurveyQuestionGroup | undefined>();
  const [selected, setSelected] = useState<string[]>([]);

  const loadActive = useCallback(async () => {
    setGroups(undefined);
    try {
      const data = await surveyAdminService.getGroups({ active: true });
      setGroups(Array.isArray(data) ? data : (data as any).rows ?? []);
    } catch { setGroups([]); }
  }, []);

  const loadArchived = useCallback(async () => {
    setArchivedGroups(undefined);
    try {
      const data = await surveyAdminService.getGroups({ active: false });
      setArchivedGroups(Array.isArray(data) ? data : (data as any).rows ?? []);
    } catch { setArchivedGroups([]); }
  }, []);

  useEffect(() => { loadActive(); }, [loadActive]);
  useEffect(() => { loadArchived(); }, [loadArchived]);

  const reload = () => { loadActive(); loadArchived(); };

  const handleEdit = (group: ISurveyQuestionGroup) => {
    setSelectedGroup(group);
    setOpenDialog(true);
  };

  const handleArchive = (group: ISurveyQuestionGroup) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Archive question set',
      message: `Archive "${group.name}"? It will no longer be available to select when creating new surveys, but any surveys already using it will be unaffected.`,
      buttonCopy: 'Archive',
      cancelButtonCopy: 'Cancel',
    }).then(async ok => {
      if (!ok) return;
      try {
        await surveyAdminService.updateGroup(group.id, { active: false } as any);
        userNotificationsContext.openSnackbar({ open: true, message: 'Question set archived', backgroundColor: 'primary.dark' });
        reload();
      } catch {
        userNotificationsContext.openSnackbar({ open: true, message: 'Failed to archive question set', backgroundColor: 'text.primary' });
      }
    });
  };

  const handleUnarchive = (group: ISurveyQuestionGroup) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Restore question set',
      message: `Restore "${group.name}"? It will become available to select when creating new surveys again.`,
      buttonCopy: 'Restore',
      cancelButtonCopy: 'Cancel',
    }).then(async ok => {
      if (!ok) return;
      try {
        await surveyAdminService.updateGroup(group.id, { active: true } as any);
        userNotificationsContext.openSnackbar({ open: true, message: 'Question set restored', backgroundColor: 'primary.dark' });
        reload();
      } catch {
        userNotificationsContext.openSnackbar({ open: true, message: 'Failed to restore question set', backgroundColor: 'text.primary' });
      }
    });
  };

  const handleDelete = (group: ISurveyQuestionGroup) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Delete question set',
      message: `Are you sure you want to permanently delete "${group.name}"?`,
      buttonCopy: 'Delete',
      cancelButtonCopy: 'Cancel',
    }).then(async confirmed => {
      if (!confirmed) return;
      try {
        await surveyAdminService.deleteGroup(group.id);
        userNotificationsContext.openSnackbar({ open: true, message: 'Question set deleted', backgroundColor: 'primary.dark' });
        reload();
      } catch (err: any) {
        const msg = err?.response?.data?.message ?? 'Failed to delete question set';
        userNotificationsContext.openSnackbar({ open: true, message: msg, backgroundColor: 'text.primary' });
      }
    });
  };

  const makeRows = (list: ISurveyQuestionGroup[], archived: boolean) =>
    list.map(g => ({
      id: g.id,
      name: g.name,
      description: g.description ?? '—',
      questions: g.surveyQuestionGroupItems?.length ?? '—',
      created: moment(g.createdAt).format('DD/MM/YYYY'),
      actions: archived ? (
        <TableActionButtons
          id={g.id}
          includeEdit={false}
          includeDelete
          onDelete={() => handleDelete(g)}
          additionalMenuItems={[{ label: 'Restore', onClick: () => handleUnarchive(g) }]}
        />
      ) : (
        <TableActionButtons
          id={g.id}
          includeEdit
          includeDelete={false}
          onEdit={() => handleEdit(g)}
          additionalMenuItems={[{ label: 'Archive', onClick: () => handleArchive(g) }]}
        />
      ),
    }));

  const Wrapper = embedded ? Box : PageOuterContainer;
  const openCreate = () => { setSelectedGroup(undefined); setOpenDialog(true); };

  return (
    <Wrapper>
      {!embedded && (
        <PageHeading
          title='Question Sets'
          includeActionButton
          actionButtonText='New Question Set'
          actionButtonIcon={IconName.Add}
          actionButtonVariant='contained'
          actionButtonHandler={openCreate}
        />
      )}
      {/* One toolbar row directly above the table: the status filter it belongs to on
          the left, the create action on the right. */}
      <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <ToggleButtonGroup
          size='small'
          exclusive
          value={tabIndex}
          onChange={(_, v) => { if (v !== null) setTabIndex(v); }}
          sx={segmentedToggleSx}
        >
          <ToggleButton value={0}>Active</ToggleButton>
          <ToggleButton value={1}>{`Archived${(archivedGroups?.length ?? 0) > 0 ? ` (${archivedGroups!.length})` : ''}`}</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction='row' spacing={1}>
          <Button variant='outlined' startIcon={<AutoAwesomeOutlined fontSize='small' />} onClick={() => setTemplateDialogOpen(true)} sx={{ whiteSpace: 'nowrap' }}>
            Start from a template
          </Button>
          {embedded && (
            <Button variant='contained' startIcon={<AddOutlined />} onClick={openCreate} sx={{ whiteSpace: 'nowrap' }}>
              New Question Set
            </Button>
          )}
        </Stack>
      </Box>
      {tabIndex === 0 && (
        <LoadingPage pageReady={!!groups}>
          <TableDisplay
            rows={makeRows(groups ?? [], false)}
            headCells={headCells}
            isMobileMode={isMobileMode}
            selected={selected}
            setSelected={setSelected}
            totalRecords={groups?.length ?? 0}
            onRowClick={(id: string) => navigate(`/surveys/question-groups/${id}`)}
          />
        </LoadingPage>
      )}
      {tabIndex === 1 && (
        <LoadingPage pageReady={!!archivedGroups}>
          <TableDisplay
            rows={makeRows(archivedGroups ?? [], true)}
            headCells={headCells}
            isMobileMode={isMobileMode}
            selected={selected}
            setSelected={setSelected}
            totalRecords={archivedGroups?.length ?? 0}
            onRowClick={(id: string) => navigate(`/surveys/question-groups/${id}`)}
          />
        </LoadingPage>
      )}
      <SurveyQuestionGroupDialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        successHandler={reload}
        existingGroup={selectedGroup}
      />
      <NewFromTemplateDialog
        open={templateDialogOpen}
        onClose={() => setTemplateDialogOpen(false)}
        onInstantiated={(groupId) => navigate(`/surveys/question-groups/${groupId}`)}
      />
    </Wrapper>
  );
};
